import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, HelpCircle, Info, FileText, Sliders } from 'lucide-react';
import { JobDescription } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

interface JobDescriptionFormProps {
  onSubmit: (job: JobDescription) => void;
}

export function JobDescriptionForm({ onSubmit }: JobDescriptionFormProps) {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [customRules, setCustomRules] = useState('');
  const isInitialMount = useRef(true);

  // Debounced submit
  const debouncedSubmit = useCallback(
    (value: string, rules: string) => {
      if (value.trim()) {
        onSubmit({
          title: "Job Description",
          description: value,
          minimumExperience: 0,
          requiredSkills: [],
          requiredQualifications: [],
          requiredLanguages: [],
          rankingRules: [],
          preferredSkills: [],
          customRules: rules.trim()
        });
      }
    },
    [onSubmit]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (description.trim() || customRules.trim()) {
      const timer = setTimeout(() => {
        debouncedSubmit(description, customRules);
      }, 750);

      return () => {
        clearTimeout(timer);
      }
    }
  }, [description, customRules, debouncedSubmit]);

  return (
    <div className="bg-gradient-to-b from-white to-gray-50 p-8 rounded-xl shadow-lg max-w-4xl mx-auto border border-gray-100">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">
            {t('jobRequirements')}
          </h2>
        </div>
        <p className="text-gray-600 ml-[52px]">
          Enter a comprehensive job description to help us find the best candidates.
        </p>
      </div>

      <div className="space-y-6">
      <div>
          <label htmlFor="description" className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-gray-700">{t('jobDescription')}</span>
            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors cursor-help" />
        </label>
          <div className="relative group">
        <textarea
          id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={12}
              className="block w-full rounded-xl border border-gray-200 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-none bg-white px-4 py-3 text-gray-700 transition-all duration-200 ease-in-out group-hover:border-gray-300"
          required
              placeholder="Enter the job description here..."
        />
            <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-200 group-hover:ring-gray-300 pointer-events-none transition-colors" />
          </div>
      </div>

      {/* New Custom Prioritization Rules textarea */}
      <div>
        <label htmlFor="customRules" className="flex items-center gap-2 mb-3">
          <span className="text-sm font-semibold text-gray-700">Custom Prioritization Rules (Optional)</span>
          <Sliders className="w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors cursor-help" />
        </label>
        <div className="relative group">
          <textarea
            id="customRules"
            value={customRules}
            onChange={(e) => setCustomRules(e.target.value)}
            rows={4}
            className="block w-full rounded-xl border border-gray-200 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-none bg-white px-4 py-3 text-gray-700 transition-all duration-200 ease-in-out group-hover:border-gray-300"
            placeholder="e.g., Prioritize candidates from MIT or Stanford; Must have PMP certification; Prefer local candidates..."
          />
          <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-200 group-hover:ring-gray-300 pointer-events-none transition-colors" />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Add special requirements or preferences that will boost a candidate's score if they match these criteria. This will significantly influence the ranking of candidates.
        </p>
      </div>

        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100">
          <div className="flex gap-3">
            <div className="mt-1">
              <div className="bg-blue-100 rounded-lg p-1.5">
                <Info className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Include in your description:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Role overview and company context',
                  'Key responsibilities and duties',
                  'Required technical skills & tools',
                  'Years of experience needed',
                  'Required education & certifications',
                  'Language requirements & levels'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
      </div>
      </div>
      </div>
  );
}